/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form, Row, Col, Modal } from 'react-bootstrap';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useTranslation } from 'react-i18next';
import { Russian } from 'flatpickr/dist/l10n/ru.js';
import { UzbekLatin } from 'flatpickr/dist/l10n/uz_latn.js';
import Flatpickr from 'react-flatpickr';
import classNames from 'classnames';

import Button from 'components/base/Button';
import systemProductService from 'service/systemProduct';
import { showAlert } from 'store/alert/alert.thunk';

interface UpdateProductStoreProps {
  selectedProductId: string | null;
  refetchActive: () => void;
  refetchNotActive: () => void;
  setOpenUpdateProduct: (show: boolean) => void;
}

const UpdateProductStore = ({
  selectedProductId,
  refetchActive,
  refetchNotActive,
  setOpenUpdateProduct
}: UpdateProductStoreProps) => {
  const { t, i18n } = useTranslation();

  const { register, getValues, control, reset, watch, setValue } = useForm();

  const dispatch: Dispatch<any> = useDispatch();
  const hasFetchedData = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(false);
  const [isDiscounter, setIsDiscounter] = useState(false);
  const [isDisabledQuantityViewType, setIsDisabledQuantityViewType] =
    useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);

  const quantityViewTypeOption = [
    { value: 'SALE', label: t('sale') },
    { value: 'SYNCHRONOUS', label: t('synchronus') },
    { value: 'STATIC', label: t('static') }
  ];

  const discoountTypeOption = [
    { value: 'AMOUNT', label: t('total') },
    { value: 'PERCENTAGE', label: t('percentage') }
  ];

  const fetchData = async () => {
    if (!selectedProductId) return;

    const resData = await systemProductService
      .getSystemProductById(selectedProductId)
      .then((res: any) => {
        const MS_IN_DAY = 24 * 60 * 60 * 1000; // 86,400,000
        const MS_IN_MONTH = 30 * MS_IN_DAY; // 2,592,000,000

        const convertMsToMonths = (ms: number): number => {
          return Math.round(ms / MS_IN_MONTH); // or use Math.floor if needed
        };

        // Example usage:
        const warrantyPeriodInMonths = convertMsToMonths(
          +res?.warranty_period || 0
        );

        const computedData: any = {
          name: res?.name,
          price: res?.price || 0,
          quantity_view_type:
            res?.sale_with_partion_code === true
              ? 'SYNCHRONOUS'
              : res?.quantity_view_type,
          status: res?.status,
          description: res?.description,
          currency_id: res?.currency_id,
          quantity: res?.quantity || 0,
          old_price: res?.old_price || 0,
          usage_conditions: res?.usage_conditions || '',
          warranty_period:
            warrantyPeriodInMonths > 0 ? warrantyPeriodInMonths : 0,
          is_discounted: res?.is_discounted || false
        };

        if (res?.sale_with_partion_code === true) {
          setIsDisabledQuantityViewType(true);
        }

        return computedData;
      });
    if (resData) {
      reset(resData);
    }
  };

  const watchedPrice = useWatch({ control, name: 'price' });
  const watchedDiscountAmount = useWatch({ control, name: 'discount_amount' });
  const watchedDiscountType = useWatch({ control, name: 'discount_type' });
  const watchedOldPrice = useWatch({ control, name: 'old_price' });
  const watchedDeactivate = useWatch({ control, name: 'deactivate' });
  const watchedIsDiscounted = useWatch({ control, name: 'is_discounted' });

  const onSubmit = () => {
    if (!selectedProductId) return;

    const data = getValues();

    setLoading(true);

    if (+data?.discount_amount === 0 || data?.discount_amount === '') {
      dispatch(
        showAlert({
          title: `${t('discount_greater_than_zero')}`
        })
      );
      setLoading(false);
      return;
    }

    const price = +data?.price || 0;
    const isDiscounter = data?.is_discounter;
    let currentPrice = price;
    const discountAmount = +data?.discount_amount || 1;
    const discountType = data?.discount_type;

    const MS_IN_DAY = 24 * 60 * 60 * 1000;
    const MS_IN_MONTH = 30 * MS_IN_DAY;
    const warrantyInMs = data?.warranty_period
      ? Number(data.warranty_period) * MS_IN_MONTH
      : 0;

    const createdData: any = {
      id: selectedProductId,
      name: data?.name || '',
      currency_id: data?.currency_id,
      price: +data?.price || 0,
      quantity_view_type: data?.quantity_view_type,
      status: data?.status,
      description: data?.description || '',
      quantity: +data?.quantity || 0,
      usage_conditions: data?.usage_conditions ?? '',
      warranty_period: warrantyInMs > 0 ? warrantyInMs : 0,
      discount_start_at: String(new Date(data.date_start[0]).getTime()),
      discount_end_at: String(new Date(data.date_finish[0]).getTime())
    };

    if (isDiscounter) {
      if (discountType === 'AMOUNT') {
        currentPrice = price - discountAmount;
      } else if (discountType === 'PERCENTAGE') {
        currentPrice = price - (price * discountAmount) / 100;
      }

      createdData.old_price = price;
      createdData.price = currentPrice;
      createdData.discount_type = discountType;
      createdData.discount_amount = discountAmount;
    } else {
      createdData.price = price;
    }

    systemProductService
      .updateSystemProduct(createdData)
      .then((res: any) => {
        if (res) {
          dispatch(
            showAlert({
              title: `${t('new_discount')} ${t('successfully_added')}`,
              type: 'success'
            })
          );
          setLoading(false);
          refetchActive();
          refetchNotActive();
          setOpenUpdateProduct(false);
        }
      })
      .catch((error: any) => {
        console.log(error);
      });
  };

  const getFlatpickrLocale = () => {
    switch (i18n.language) {
      case 'ru':
        return Russian;
      case 'uz':
        return UzbekLatin;
      default:
        return undefined;
    }
  };

  useEffect(() => {
    if (selectedProductId !== '' && !hasFetchedData.current) {
      fetchData();
      hasFetchedData.current = true;
    }
  }, [selectedProductId]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      setTimeout(() => inputRef.current?.select(), 500);
    }
  }, []);

  useEffect(() => {
    const price = parseFloat(watchedPrice || 0);
    const discountAmount = parseFloat(watchedDiscountAmount || 0);
    let discountedPrice = price;

    if (isDiscounter && !isNaN(price) && !isNaN(discountAmount)) {
      if (watchedDiscountType === 'AMOUNT') {
        discountedPrice = price - discountAmount;
      } else if (watchedDiscountType === 'PERCENTAGE') {
        discountedPrice = price - (price * discountAmount) / 100;
      }
    }

    setValue('current_price', discountedPrice.toFixed(2));
  }, [
    watchedPrice,
    watchedDiscountAmount,
    watchedDiscountType,
    isDiscounter,
    setValue
  ]);

  const handleDeactivateConfirm = (confirm: boolean) => {
    if (confirm) {
      systemProductService
        ?.createDiscountDeactivate({
          id: selectedProductId
        })
        .then((res: any) => {
          if (res) {
            setValue('price', res?.price || 0);
            setValue('old_price', res?.price || 0);
            setValue('deactivate', true);
            setValue('is_discounter', true);
            setIsDiscounter(true);
          }
        });
    } else {
      setValue('deactivate', false);
      setValue('is_discounter', false);
      setIsDiscounter(false);
    }
    setShowDeactivateModal(false);
  };

  return (
    <>
      <Form
        noValidate
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <Row className="mb-2">
          <Col>
            <Form.Floating>
              <Form.Control
                {...register('name')}
                disabled
                type="text"
                placeholder={t('product_names')}
                onFocus={e => e.target.select()}
                autoComplete="off"
              />
              <label htmlFor="floatingInputCustom">{t('product_names')}</label>
            </Form.Floating>
          </Col>
        </Row>

        <Row className="mb-2">
          <Col className="d-flex gap-2">
            <Form.Floating className="flex-1">
              <Form.Control
                {...register('price')}
                disabled
                type="text"
                placeholder={t('price')}
                onFocus={e => e.target.select()}
                autoComplete="off"
              />
              <label htmlFor="floatingInputCustom">{t('price')}</label>
            </Form.Floating>
            {Number(watchedOldPrice) !== 0 && (
              <Form.Floating className="md-4">
                <Form.Control
                  disabled
                  {...register(`old_price`)}
                  type="number"
                  placeholder={t('old_price')}
                  onFocus={e => e.target.select()}
                />
                <label htmlFor="floatingInputCustom">{t('old_price')}</label>
              </Form.Floating>
            )}
          </Col>
        </Row>

        <Row className="mb-2">
          <Col className="d-flex gap-2">
            <div className="react-select-container flex-1">
              <Controller
                name="quantity_view_type"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Form.Floating>
                    <Form.Select
                      {...field}
                      disabled={isDisabledQuantityViewType}
                    >
                      {!field.value && (
                        <option value="" disabled hidden></option>
                      )}
                      {quantityViewTypeOption?.map((option: any) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>

                    <label htmlFor="floatingInputCustom">
                      {t('remainder_type')}
                    </label>
                  </Form.Floating>
                )}
              />
            </div>
            {watch('quantity_view_type') === 'STATIC' && (
              <Form.Floating className="md-4">
                <Form.Control
                  {...register(`quantity`)}
                  type="number"
                  placeholder={t('quantity')}
                  onFocus={e => e.target.select()}
                />
                <label htmlFor="floatingInputCustom">{t('quantity')}</label>
              </Form.Floating>
            )}
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Form.Floating>
              <Form.Control
                {...register('warranty_period')}
                type="number"
                placeholder={t('warranty_period')}
                onFocus={e => e.target.select()}
                autoComplete="off"
              />
              <label htmlFor="floatingInputCustom">
                {`${t('warranty_period')} (${t('month')})`}
              </label>
            </Form.Floating>
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Form.Floating className="md-4">
              <Form.Control
                {...register('usage_conditions')}
                as="textarea"
                style={{ height: '100px' }}
                placeholder={t('usage_conditions')}
                onFocus={e => e.target.select()}
              />
              <label htmlFor="floatingInputCustom">
                {t('usage_conditions')}
              </label>
            </Form.Floating>
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Form.Floating className="md-4">
              <Form.Control
                {...register('description')}
                as="textarea"
                style={{ height: '100px' }}
                placeholder={t('descriptions')}
                onFocus={e => e.target.select()}
              />
              <label htmlFor="floatingInputCustom">{t('descriptions')}</label>
            </Form.Floating>
          </Col>
        </Row>

        <Row className="mb-2">
          <Col className="d-flex gap-3">
            <Controller
              name="is_discounter"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <Form.Check
                  type="switch"
                  label={t('new_discount')}
                  className="cursor-pointer"
                  checked={field.value}
                  disabled={!watchedDeactivate && watchedIsDiscounted}
                  onChange={e => {
                    field.onChange(e.target.checked);
                    setIsDiscounter(e.target.checked);
                  }}
                />
              )}
            />
            <Controller
              name="deactivate"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <Form.Check
                  type="switch"
                  label={t('deactivate_previous_discount')}
                  className="cursor-pointer"
                  checked={field.value}
                  disabled={field.value || !watchedIsDiscounted} // Disable switch when it is true
                  onChange={e => {
                    if (e.target.checked) {
                      setShowDeactivateModal(true);
                    }
                  }}
                />
              )}
            />
          </Col>
        </Row>

        {isDiscounter && (
          <Row className="mb-2">
            <Col className="d-flex gap-2">
              <div className="react-select-container flex-1">
                <Controller
                  name="discount_type"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Form.Floating>
                      <Form.Select {...field}>
                        {!field.value && (
                          <option value="" disabled hidden></option>
                        )}
                        {discoountTypeOption?.map((option: any) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>

                      <label htmlFor="floatingInputCustom">
                        {t('discount_type')}
                      </label>
                    </Form.Floating>
                  )}
                />
              </div>
              <Form.Floating className="md-4">
                <Form.Control
                  {...register(`discount_amount`)}
                  type="number"
                  placeholder={t('discount_pratcentage_or_amount')}
                  onFocus={e => e.target.select()}
                  step={watchedDiscountType === 'PERCENTAGE' ? 0.01 : 1}
                  onChange={e => {
                    const value = e.target.value.replace(',', '.');
                    let parsedValue: number;

                    if (watchedDiscountType === 'PERCENTAGE') {
                      // Ensure integer by removing decimals
                      parsedValue =
                        parseFloat(parseFloat(value).toFixed(4)) || 0;
                    } else {
                      parsedValue = parseFloat(value) || 0;
                    }

                    const maxValue =
                      watchedDiscountType === 'PERCENTAGE'
                        ? 100
                        : Number(watchedPrice) || 0;
                    // Clamp the value between 0 and maxValue
                    const clampedValue = Math.max(
                      0,
                      Math.min(parsedValue, maxValue)
                    );
                    setValue('discount_amount', clampedValue, {
                      shouldValidate: true
                    });
                  }}
                />
                <label htmlFor="floatingInputCustom">
                  {t('discount_pratcentage_or_amount')}
                </label>
              </Form.Floating>
            </Col>
          </Row>
        )}

        {isDiscounter && (
          <Row className="mb-2">
            <Col>
              <Form.Floating>
                <Form.Control
                  disabled
                  {...register('current_price', { valueAsNumber: true })}
                  type="text"
                  placeholder={t('current_price')}
                  onFocus={e => e.target.select()}
                  autoComplete="off"
                />
                <label htmlFor="floatingInputCustom">
                  {t('current_price')}
                </label>
              </Form.Floating>
            </Col>
          </Row>
        )}

        {isDiscounter && (
          <>
            <Form.Label>{t('date_start')}</Form.Label>
            <Row className="mb-2">
              <Col>
                <Controller
                  name="date_start"
                  control={control}
                  render={({ field }) => (
                    <Form.Floating>
                      <Flatpickr
                        className={classNames('form-control')}
                        style={{ height: '48px' }}
                        options={{
                          nextArrow: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M96 480c-8.188 0-16.38-3.125-22.62-9.375c-12.5-12.5-12.5-32.75 0-45.25L242.8 256L73.38 86.63c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0l192 192c12.5 12.5 12.5 32.75 0 45.25l-192 192C112.4 476.9 104.2 480 96 480z"/></svg>`,
                          prevArrow: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M224 480c-8.188 0-16.38-3.125-22.62-9.375l-192-192c-12.5-12.5-12.5-32.75 0-45.25l192-192c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25L77.25 256l169.4 169.4c12.5 12.5 12.5 32.75 0 45.25C240.4 476.9 232.2 480 224 480z"/></svg>`,
                          locale: getFlatpickrLocale(),
                          monthSelectorType: 'static',
                          onDayCreate: (...[, , , dayElem]) => {
                            if (
                              dayElem.dateObj.getDay() === 5 ||
                              dayElem.dateObj.getDay() === 6
                            ) {
                              dayElem.className += ' weekend-days';
                            }
                          },
                          dateFormat: 'd-m-Y H:i',
                          enableTime: true,
                          static: true,
                          disableMobile: true,
                          defaultHour: new Date().getHours(),
                          defaultMinute: new Date().getMinutes(),
                          minDate: 'today',
                          onChange: (selectedDates, dateStr, instance) => {
                            const today = new Date();
                            const selectedDate = selectedDates[0];
                            // If the selected date is today, set minTime to current time
                            if (
                              selectedDate &&
                              selectedDate.toDateString() ===
                                today.toDateString()
                            ) {
                              instance.set(
                                'minTime',
                                `${today.getHours()}:${today.getMinutes()}`
                              );
                            } else {
                              instance.set('minTime', '00:00'); // No time restriction for future dates
                            }
                          }
                        }}
                        placeholder={t('date_start')}
                        {...field}
                      />
                    </Form.Floating>
                  )}
                />
              </Col>
            </Row>
          </>
        )}

        {isDiscounter && (
          <>
            <Form.Label>{t('date_end')}</Form.Label>
            <Row className="mb-3">
              <Col>
                <Controller
                  name="date_finish"
                  control={control}
                  render={({ field }) => (
                    <Form.Floating>
                      <Flatpickr
                        className={classNames('form-control')}
                        style={{ height: '48px' }}
                        options={{
                          nextArrow: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M96 480c-8.188 0-16.38-3.125-22.62-9.375c-12.5-12.5-12.5-32.75 0-45.25L242.8 256L73.38 86.63c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0l192 192c12.5 12.5 12.5 32.75 0 45.25l-192 192C112.4 476.9 104.2 480 96 480z"/></svg>`,
                          prevArrow: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M224 480c-8.188 0-16.38-3.125-22.62-9.375l-192-192c-12.5-12.5-12.5-32.75 0-45.25l192-192c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25L77.25 256l169.4 169.4c12.5 12.5 12.5 32.75 0 45.25C240.4 476.9 232.2 480 224 480z"/></svg>`,
                          locale: getFlatpickrLocale(),
                          monthSelectorType: 'static',
                          onDayCreate: (...[, , , dayElem]) => {
                            if (
                              dayElem.dateObj.getDay() === 5 ||
                              dayElem.dateObj.getDay() === 6
                            ) {
                              dayElem.className += ' weekend-days';
                            }
                          },
                          dateFormat: 'd-m-Y H:i',
                          enableTime: true,
                          static: true,
                          disableMobile: true,
                          defaultHour: new Date().getHours(),
                          defaultMinute: new Date().getMinutes(),
                          minDate: 'today',
                          onChange: (selectedDates, dateStr, instance) => {
                            const today = new Date();
                            const selectedDate = selectedDates[0];
                            // If the selected date is today, set minTime to current time
                            if (
                              selectedDate &&
                              selectedDate.toDateString() ===
                                today.toDateString()
                            ) {
                              instance.set(
                                'minTime',
                                `${today.getHours()}:${today.getMinutes()}`
                              );
                            } else {
                              instance.set('minTime', '00:00'); // No time restriction for future dates
                            }
                          }
                        }}
                        placeholder={t('date_end')}
                        {...field}
                      />
                    </Form.Floating>
                  )}
                />
              </Col>
            </Row>
          </>
        )}

        <Button
          style={{ height: '48px', marginTop: 'auto' }}
          variant="primary"
          type="button"
          loading={loading}
          loadingPosition="start"
          onClick={() => onSubmit()}
        >
          {t('add_product')}
        </Button>

        <Modal
          show={showDeactivateModal}
          onHide={() => handleDeactivateConfirm(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{t('confirm_deactivation')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>{t('deactivate_discount_confirm_message')}</Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => handleDeactivateConfirm(false)}
            >
              {t('no')}
            </Button>
            <Button
              variant="primary"
              onClick={() => handleDeactivateConfirm(true)}
            >
              {t('yes')}
            </Button>
          </Modal.Footer>
        </Modal>
      </Form>
    </>
  );
};

export default UpdateProductStore;
